












































































import {
  defineComponent,
  computed,
  ref,
  watch,
  PropType
} from '~/utils/nuxt3-migration'
import { BubbleMenu, Editor } from '@tiptap/vue-2'
import { Command, ToolbarConfig } from '~/models/rich-text/types'
import CFormSelect from '~/components/shared/configurable/form/select/CFormSelect.vue'

export default defineComponent({
  components: {
    CEditorImageSelectModal: () => import('./CEditorImageSelectModal.vue'),
    CRichTextEditorLinkModal: () => import('./CRichTextEditorLinkModal.vue'),
    BubbleMenu,
    CFormSelect
  },
  props: {
    editor: {
      type: Object,
      required: true
    },
    useBubbleMenu: {
      type: Boolean,
      default: false
    },
    toolbarGroups: {
      type: Array as PropType<ToolbarConfig[]>,
      required: true,
      default() {
        return []
      }
    }
  },
  setup(props: {
    editor: Editor
    useBubbleMenu: boolean
    toolbarGroups: ToolbarConfig[]
  }) {
    const imageModalRef = ref<InstanceType<
      typeof import('./CEditorImageSelectModal.vue').default
    > | null>(null)
    const linkModalRef = ref<InstanceType<
      typeof import('./CRichTextEditorLinkModal.vue').default
    > | null>(null)

    const selectedHeading = ref<string | number>('text')

    const headingGroup = computed(() => {
      return props.toolbarGroups.find(group => group.name === 'heading')
    })

    const headingGroupPosition = computed(() => {
      return props.toolbarGroups.findIndex(group => group.name === 'heading')
    })

    const commands = computed(() => {
      return props.toolbarGroups.map(group => group.commands).flat()
    })

    const isImageModalShown = computed(() => {
      return commands.value.filter(e => e.namespace === 'image').length
    })

    const isLinkModalShown = computed(() => {
      return commands.value.filter(e => e.namespace === 'link').length
    })

    watch(
      () => props.editor.isActive('paragraph'),
      () => {
        if (props.editor.isActive('paragraph')) selectedHeading.value = 'text'
      }
    )

    function isActive(command: Command) {
      return props.editor.isActive(command.namespace, command.options)
    }

    function runCommand(command: Command | undefined) {
      if (!command) {
        props.editor
          .chain()
          .focus()
          .setParagraph()
          .run()
        return
      }

      if (command.namespace === 'image') {
        showImageModal(command)
      } else if (command.namespace === 'link') {
        showLinkModal(command)
      } else {
        const chain = props.editor.chain().focus()
        return (chain[command.actionName as keyof typeof chain] as Function)(
          command.options
        ).run()
      }
    }
    function setHeading() {
      const command = commands.value.find(
        cmd => cmd.options && cmd.options.level === selectedHeading.value
      )
      runCommand(command)
    }
    function getIcon(command: Command) {
      if (!command.icon) return null
      if (isActive(command) && command.icon.solid) {
        return command.icon.solid
      }
      return command.icon.regular
    }
    function showImageModal(cmd: Command) {
      if (imageModalRef.value) imageModalRef.value.showModal(cmd)
    }
    function showLinkModal(cmd: Command) {
      if (linkModalRef.value) linkModalRef.value.showModal(cmd)
    }

    function getHeadingOptionClass(level: number | undefined) {
      if (!level) return ''
      switch (level) {
        case 1:
          return 'tw-font-bold tw-text-2xl'
        case 2:
          return 'tw-font-semibold tw-text-xl'
        case 3:
          return 'tw-font-medium tw-text-lg'
        default:
          return 'tw-text-base'
      }
    }

    function preventEditorLoseFocus(e: MouseEvent) {
      if (e) e.preventDefault()
    }

    return {
      isActive,
      isImageModalShown,
      isLinkModalShown,
      runCommand,
      showLinkModal,
      showImageModal,
      selectedHeading,
      getHeadingOptionClass,
      imageModalRef,
      linkModalRef,
      headingGroup,
      headingGroupPosition,
      setHeading,
      getIcon,
      preventEditorLoseFocus
    }
  }
})
